export const LegalMeta = {
  termsLastUpdated: "2025 Sept 12",
};

export const MISSING_DISTRIBUTION_ID: string =
  "Missing or invalid distribution & diagnostic IDs in the URL. Please use the correct link provided.";
export const CLOSED_DIAGNOSTIC: string =
  "This diagnostic is closed. You cannot answer any more questions.";
export const MINIMUM_ANSWER_LENGTH: number = 1;
export const MAX_ANSWER_LENGTH: number = 1024;
export const MINIMUM_ANSWER_LENGTH_REQUIRED: string =
  "We need a bit more information. Could you elaborate?"; //`Minimum ${MINIMUM_ANSWER_LENGTH} characters are required.`;
export const MAXIMUM_ANSWER_LENGTH_ALLOWED: string = `Maximum ${MAX_ANSWER_LENGTH} characters are allowed.`;
export const ANSWER_IS_REQUIRED: string = "An answer is required.";
export const INVALID_URL: string =
  "The URL you provided is not valid. Please check your URL\u00A0again.\nIf you believe you received this in error, please contact the Diagnostic\u00A0Administrator.";
export const DISTRIBUTION_LOCKED_TITLE: string = "Interview Closed";
export const DISTRIBUTION_LOCKED_MESSAGE: string =
  "This interview is closed and no longer accepting responses. If you think this is a mistake or still need to take part, please contact the Diagnostic Administrator.";
export const VOICE_ON_LABEL = "Voice: On";
export const VOICE_OFF_LABEL = "Voice: Off";
export const VOICE_TOOLTIP =
  "Select a language to answer using your microphone instead of typing.";
export const VOICE_ON_HINT = "Speak your answer and send when you're ready.";
export const VOICE_OFF_HINT = "Type your answer and press Submit.";
export const DEFAULT_VOICE_LOCALE = "en-US";

export interface VoiceOption {
  key: string;
  text: string;
  locale: string;
  flag: string;
  voiceName: string;
  finalEndings: string[];
  demographicsEnding: string;
  likertPrompt: string;
  multipleChoicePrompt: string;
  multipleChoicePromptSingle: string;
  multipleChoicePlaceholder: string;
  responsePlaceholder: string;
  demographicsPlaceholder: string;
  ratingSectionDescription: string;
  statementCounter: string;
  questionCounter: string;
  multipleChoiceDescription: string;
}

export const VOICE_OPTIONS: VoiceOption[] = [
  {
    key: "disabled",
    text: "Disabled",
    flag: "🎤",
    locale: "",
    voiceName: "",
    finalEndings: [
      "Thank you for your comments on",
      "and appreciate you taking the time for this interview.",
      "I have no further questions. You can now close this browser window.",
      "Thank you for taking the time for this interview.",
    ],
    demographicsEnding:
      "Thank you for your responses to the demographic questions",
    likertPrompt: "How much do you agree with the statement above?",
    multipleChoicePrompt: "Select all that apply.",
    multipleChoicePromptSingle: "Select one option.",
    multipleChoicePlaceholder: "Please select",
    responsePlaceholder:
      "Please enter your honest response here and be as thorough as possible.",
    demographicsPlaceholder: "Please enter your response here.",
    multipleChoiceDescription:
      "For this section, you’ll choose from a set of options. I’ll let you know whether to choose just one or select all that apply.",
    ratingSectionDescription:
      "This section uses statements rather than open questions. For each one, tell me how much you agree — or choose N/A if it doesn't apply to you.",
    statementCounter: "Statement {current} of {total}",
    questionCounter: "Question {current} of {total}",
  },
  {
    key: "en-US",
    text: "English",
    flag: "🇺🇸",
    locale: "en-US",
    voiceName: "en-US-JennyMultilingualNeural",
    finalEndings: [
      "Thank you for your comments on",
      "and appreciate you taking the time for this interview.",
      "I have no further questions. You can now close this browser window.",
      "Thank you for taking the time for this interview.",
    ],
    demographicsEnding:
      "Thank you for your responses to the demographic questions",
    likertPrompt: "How much do you agree with the statement above?",
    multipleChoicePrompt: "Select all that apply.",
    multipleChoicePromptSingle: "Select one option.",
    multipleChoicePlaceholder: "Please select",
    responsePlaceholder:
      "Please enter your honest response here and be as thorough as possible.",
    demographicsPlaceholder: "Please enter your response here.",
    ratingSectionDescription:
      "This section uses statements rather than open questions. For each one, tell me how much you agree — or choose N/A if it doesn't apply to you.",
    statementCounter: "Statement {current} of {total}",
    questionCounter: "Question {current} of {total}",
    multipleChoiceDescription:
      "For this section, you’ll choose from a set of options. I’ll let you know whether to choose just one or select all that apply.",
  },
  {
    key: "es-ES",
    text: "Spanish",
    flag: "🇪🇸",
    locale: "es-ES",
    voiceName: "es-ES-ElviraNeural",
    finalEndings: [
      "Gracias por sus comentarios sobre",
      "y aprecio que se haya tomado el tiempo para esta entrevista.",
      "No tengo más preguntas. Ahora puede cerrar la ventana del navegador.",
      "Gracias por tomarse el tiempo para esta entrevista.",
    ],
    demographicsEnding:
      "Gracias por sus respuestas a las preguntas demográficas",
    likertPrompt: "¿Cuánto está de acuerdo con la afirmación anterior?",
    multipleChoicePrompt: "Seleccione todas las que correspondan.",
    multipleChoicePromptSingle: "Seleccione una opción.",
    multipleChoicePlaceholder: "Seleccione",
    responsePlaceholder:
      "Por favor, introduzca su respuesta sincera aquí y sea lo más detallado posible.",
    demographicsPlaceholder: "Por favor, introduzca su respuesta aquí.",
    ratingSectionDescription:
      "Esta sección utiliza afirmaciones en lugar de preguntas abiertas. Para cada una, indíqueme cuánto está de acuerdo, o elija N/A si no se aplica en su caso.",
    statementCounter: "Afirmación {current} de {total}",
    questionCounter: "Pregunta {current} de {total}",
    multipleChoiceDescription:
      "En esta sección, elegirá entre un conjunto de opciones. Le indicaré si debe elegir solo una o seleccionar todas las que correspondan.",
  },
  {
    key: "fr-FR",
    text: "French",
    flag: "🇫🇷",
    locale: "fr-FR",
    voiceName: "fr-FR-DeniseNeural",
    finalEndings: [
      "Merci pour vos commentaires sur",
      "et je vous remercie d'avoir pris le temps pour cet entretien.",
      "Je n'ai pas d'autres questions. Vous pouvez maintenant fermer cette fenêtre de navigateur.",
      "Merci d'avoir pris le temps pour cet entretien.",
    ],
    demographicsEnding: "Merci pour vos réponses aux questions démographiques",
    likertPrompt:
      "Dans quelle mesure êtes-vous d'accord avec l'affirmation ci-dessus ?",
    multipleChoicePrompt: "Sélectionnez toutes les réponses applicables.",
    multipleChoicePromptSingle: "Sélectionnez une option.",
    multipleChoicePlaceholder: "Veuillez sélectionner",
    responsePlaceholder:
      "Veuillez saisir votre réponse honnête ici et être aussi complet que possible.",
    demographicsPlaceholder: "Veuillez saisir votre réponse ici.",
    ratingSectionDescription:
      "Cette section utilise des affirmations plutôt que des questions ouvertes. Pour chacune, indiquez-moi dans quelle mesure vous êtes d'accord, ou choisissez N/A si cela ne s'applique pas à vous.",
    statementCounter: "Affirmation {current} sur {total}",
    questionCounter: "Question {current} sur {total}",
    multipleChoiceDescription:
      "Dans cette section, vous choisirez parmi un ensemble d'options. Je vous indiquerai s'il faut en choisir une seule ou sélectionner toutes celles qui s'appliquent.",
  },
  {
    key: "ar-SA",
    text: "Arabic",
    flag: "🇸🇦",
    locale: "ar-SA",
    voiceName: "ar-AE-HamdanNeural",
    finalEndings: [
      "شكراً على تعليقاتك بشأن",
      "وأقدر لك تخصيص الوقت لهذه المقابلة.",
      "ليس لدي المزيد من الأسئلة. يمكنك الآن إغلاق نافذة المتصفح هذه.",
      "شكراً لك على تخصيص وقتك لهذه المقابلة.",
    ],
    demographicsEnding: "شكراً على إجاباتك على الأسئلة الديموغرافية",
    likertPrompt: "إلى أي مدى توافق على العبارة أعلاه؟",
    multipleChoicePrompt: "اختر كل ما ينطبق.",
    multipleChoicePromptSingle: "اختر خيارًا واحدًا.",
    multipleChoicePlaceholder: "الرجاء الاختيار",
    responsePlaceholder:
      "يرجى إدخال ردك الصادق هنا وأن تكون شاملاً قدر الإمكان.",
    demographicsPlaceholder: "يرجى إدخال ردك هنا.",
    ratingSectionDescription:
      "يستخدم هذا القسم عبارات بدلاً من أسئلة مفتوحة. بالنسبة لكل عبارة، أخبرني إلى أي مدى توافق عليها — أو اختر غير منطبق إذا لم تكن تنطبق عليك.",
    statementCounter: "العبارة {current} من {total}",
    questionCounter: "السؤال {current} من {total}",
    multipleChoiceDescription:
      "في هذا القسم، ستختار من بين مجموعة من الخيارات. سأخبرك ما إذا كان عليك اختيار خيار واحد فقط أو تحديد كل ما ينطبق.",
  },
  {
    key: "pt-BR",
    text: "Brazilian Portuguese",
    flag: "🇧🇷",
    locale: "pt-BR",
    voiceName: "pt-BR-FranciscaNeural",
    finalEndings: [
      "Obrigado por seus comentários sobre",
      "e agradeço por ter dedicado seu tempo a esta entrevista.",
      "Não tenho mais perguntas. Você já pode fechar a janela do navegador.",
      "Obrigado por dedicar seu tempo a esta entrevista.",
    ],
    demographicsEnding: "Obrigado por suas respostas às perguntas demográficas",
    likertPrompt: "O quanto você concorda com a afirmação acima?",
    multipleChoicePrompt: "Selecione todas as opções aplicáveis.",
    multipleChoicePromptSingle: "Selecione uma opção.",
    multipleChoicePlaceholder: "Selecione",
    responsePlaceholder:
      "Por favor, insira sua resposta sincera aqui e seja o mais detalhado possível.",
    demographicsPlaceholder: "Por favor, insira sua resposta aqui.",
    ratingSectionDescription:
      "Esta seção usa afirmações em vez de perguntas abertas. Para cada uma, diga-me o quanto você concorda — ou escolha N/A se não se aplicar a você.",
    statementCounter: "Afirmação {current} de {total}",
    questionCounter: "Pergunta {current} de {total}",
    multipleChoiceDescription:
      "Nesta seção, você escolherá entre um conjunto de opções. Vou informar se você deve escolher apenas uma ou selecionar todas as aplicáveis.",
  },
];
